import { useCallback, useEffect, useState } from 'react'

interface CacheItem<T> {
  data: T
  timestamp: number
  ttl: number
}

interface CacheOptions {
  ttl?: number // Time to live em milissegundos
  maxSize?: number // Tamanho máximo do cache
}

class Cache {
  private cache = new Map<string, CacheItem<unknown>>()
  private maxSize: number
  private defaultTTL: number

  constructor(options: CacheOptions = {}) {
    this.maxSize = options.maxSize || 100
    this.defaultTTL = options.ttl || 5 * 60 * 1000 // 5 minutos por padrão
  }

  set<T>(key: string, data: T, ttl?: number): void {
    // Remover item se cache estiver cheio
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value
      this.cache.delete(firstKey)
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL,
    })
  }

  get<T>(key: string): T | null {
    const item = this.cache.get(key)
    
    if (!item) return null

    // Verificar se o item expirou
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key)
      return null
    }

    return item.data as T
  }

  has(key: string): boolean {
    return this.get(key) !== null
  }

  delete(key: string): boolean {
    return this.cache.delete(key)
  }

  clear(): void {
    this.cache.clear()
  }

  size(): number {
    return this.cache.size
  }

  // Limpar itens expirados
  cleanup(): void {
    const now = Date.now()
    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        this.cache.delete(key)
      }
    }
  }

  // Gerar chave de cache baseada em parâmetros
  static generateKey(prefix: string, params: Record<string, unknown>): string {
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}:${params[key]}`)
      .join('|')
    
    return `${prefix}:${sortedParams}`
  }
}

// Instância global do cache
export const cache = new Cache({
  ttl: 5 * 60 * 1000, // 5 minutos
  maxSize: 100,
})

// Hook para usar cache com React
export function useCache<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: { ttl?: number; enabled?: boolean } = {}
): { data: T | null; loading: boolean; error: Error | null; refetch: () => void } {
  const [data, setData] = useState<T | null>(cache.get(key))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const { ttl, enabled = true } = options

  const fetchData = useCallback(async () => {
    if (!enabled) return

    setLoading(true)
    setError(null)

    try {
      const result = await fetcher()
      cache.set(key, result, ttl)
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Erro desconhecido'))
    } finally {
      setLoading(false)
    }
  }, [key, fetcher, ttl, enabled])

  useEffect(() => {
    if (!cache.has(key)) {
      fetchData()
    }
  }, [key, fetchData])

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  }
}

// Utilitários para cache
export const cacheUtils = {
  // Limpar cache periodicamente
  startCleanup: () => {
    setInterval(() => {
      cache.cleanup()
    }, 60 * 1000) // Limpar a cada minuto
  },

  // Cache para serviços
  services: {
    get: (filters: Record<string, unknown>) => 
      cache.get(Cache.generateKey('services', filters)),
    set: (filters: Record<string, unknown>, data: unknown) => 
      cache.set(Cache.generateKey('services', filters), data),
  },

  // Cache para categorias
  categories: {
    get: () => cache.get('categories'),
    set: (data: unknown) => cache.set('categories', data, 10 * 60 * 1000), // 10 minutos
  },

  // Cache para subcategorias
  subcategories: {
    get: () => cache.get('subcategories'),
    set: (data: unknown) => cache.set('subcategories', data, 10 * 60 * 1000), // 10 minutos
  },
} 